import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";

// styles
import styles from "./AdvancedSearchV2.module.scss";

import { BusinessItem } from "types/results-panel.types";

// components
import FilterFlyoutBar from "components/FilterFlyoutBar";
import AppliedFilterSentence from "components/AppliedFilterSentence";
import SearchBarNew from "components/SearchBarNew/SearchBarNew";
import EmptyQuery from "components/EmptyQuery/EmptyQuery";
import NoResultFound from "components/NoResultFound/NoResultFound";
import SortBy from "components/SortBy/SortBy";
import ResultTable from "components/ResultTable/ResultTable";
import TableSkeleton from "components/ResultTable/TableSkeleton";
// import ExportModal from "layouts/MainLayout/Modal/ExportModal";
import EditTableModal from "layouts/MainLayout/Modal/EditTableModal";
import { RoundedButton } from "components/LibraryComponents/Components";
import { BarChartIcon } from "components/icons";
import ExportBy from "components/ExportBy/ExportBy";
import { Icon } from "@trussworks/react-uswds";
import { SimpleToopTip } from "components/Tooltip";
import NotificationBanner from "components/NotifcationBanner/NotificationBanner";

// store
import useFiltersStore from "store/useFiltersStoreNew";
import { useSearchStore } from "store/searchStore";

// query
import useResultQueryV2 from "./queries";


const SEARCH_ADDRESS_LIMIT = 150000;

export default function AdvancedSearchV2() {
  const { filters, areFiltersApplied } = useFiltersStore((state) => ({
    areFiltersApplied: state.areFiltersApplied(),
    filters: state.filters,
  }));

  const { resultSorting, setResultSorting } = useSearchStore();

  const [loading, setLoading] = useState(false);
  const [sortedTableData, setSortedTableData] = useState<BusinessItem[]>([]);

  const {
    data,
    isLoading,
    isError
  } =
    useResultQueryV2();

  useEffect(() => {
    if (!areFiltersApplied || !isLoading) {
      setLoading(false);
    } else if (areFiltersApplied || isLoading ) {
      setLoading(true);
    }
  }, [areFiltersApplied, isLoading])

  const results = data?.results ? data.results : []

  const [, setSearchParams] = useSearchParams();

  const showTable = !loading && results && results.length > 0 && !isError && areFiltersApplied;
  const showNotFound =
    (!loading && areFiltersApplied && results && results.length === 0) || isError && areFiltersApplied;

  const tableData = useMemo(() => { return results}, [results])

  const tableDataTotalPages = Math.ceil(tableData.length / 25);
  const tableDataTotalCount = tableData.length;

  const isOverLimit = tableDataTotalCount >= SEARCH_ADDRESS_LIMIT;

  useEffect(() => {
    // reset page index when filters or sorting change
    setSearchParams({ page: "0" });
  }, [filters, resultSorting]);

  return (
    <>
      <Helmet>
        <title>Advanced Search | Small Business Search</title>
      </Helmet>

      <div className={styles["advanced-search-root"]}>
        <div className={styles["container"]}>
          <NotificationBanner />
          <div className={styles["search-bar-wrapper"]}>
            <SearchBarNew />
          </div>

          <div className={styles["flyout-bar-section"]}>
            <FilterFlyoutBar />
          </div>

          <hr className={styles["divider"]} />

          <div hidden={!areFiltersApplied} className={styles["table-controls"]}>
            <div className={styles["top-section"]}>
              {!loading && areFiltersApplied && (
                <>
                  {!isOverLimit ? (
                    <h3>
                      There are{" "}
                      <strong>
                        {tableDataTotalCount.toLocaleString("en-US")}
                      </strong>{" "}
                      small businesses
                    </h3>
                  ) : (
                    <div className={styles["over-limit"]}>
                      <h3>
                        There are over <strong>150,000</strong> small businesses
                      </h3>

                      <SimpleToopTip
                        title={
                          <div className={styles["tooltip-content"]}>
                            <p className={styles["heading"]}>
                              150,000 result limit
                            </p>
                            <p className={styles["desc"]}>
                              Your current filter choices or searched term has
                              returned too many results to display. Refine your
                              search with more filters to see a complete list of
                              businesses.
                            </p>
                          </div>
                        }
                        placement="top"
                        className={styles["tooltip-wrapper"]}
                      >
                        <span className={styles["icon-wrapper"]}>
                          <Icon.InfoOutline
                            aria-label="Over limit results returned"
                            className={styles["info-icon"]}
                          />
                        </span>
                      </SimpleToopTip>
                    </div>
                  )}
                </>
              )}

              {loading && (
                <Skeleton
                  variant="text"
                  className={styles["skeleton"]}
                  animation="wave"
                />
              )}

              {!showNotFound && (
                <div className={styles["group"]}>
                  <EditTableModal isLoading={loading} />
                  <ExportBy data={sortedTableData} isLoading={loading} />
                </div>
              )}
            </div>

            <div className={styles["bottom-section"]}>
              {!loading && <AppliedFilterSentence />}

              {loading && (
                <Skeleton
                  variant="text"
                  className={styles["skeleton"]}
                  animation="wave"
                />
              )}

              {!showNotFound && (
                <div className={styles["group"]}>
                  <Link to="/market-insights">
                    <RoundedButton variant="ghost" disabled={loading}>
                      <BarChartIcon />
                      Market Insights
                    </RoundedButton>
                  </Link>

                  <SortBy
                    selectedSort={resultSorting}
                    setSelectedSort={setResultSorting}
                    isLoading={loading}
                  />
                </div>
              )}
            </div>
          </div>

          {!areFiltersApplied && (
            <EmptyQuery
              content="Choose filters or search for a term to start finding small businesses"
              style={{ marginTop: 88 }}
            />
          )}

          {showNotFound && (
            <NoResultFound content="There are no small businesses that match your search terms. Try clearing some of your filters, or starting over." />
          )}
        </div>

        {loading && <TableSkeleton />}

        {showTable && (
          <div className={styles["table-wrapper"]}>
            <ResultTable
              data={tableData}
              sortType={resultSorting.value}
              rootClassname={styles["table-root-wrapper"]}
              totalPages={tableDataTotalPages}
              setSortedTableData={setSortedTableData}
            />
          </div>
        )}
      </div>
    </>
  );
}
